import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Send, AlertTriangle, Store as StoreIcon, Package, Calendar, User2, Clock, MessageSquare, Sparkles, History, Camera, Upload, X } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { ticketsApi, storesApi, usersApi } from '../lib/api';
import PublicHeader from '../components/PublicHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const STATUS_LABELS = {
  open: 'Aperto',
  in_progress: 'In lavorazione',
  waiting_parts: 'In attesa ricambi',
  resolved: 'Risolto',
  closed: 'Chiuso',
  item_resolved: 'Oggetto risolto',
};
const STATUS_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  waiting_parts: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-50 text-slate-600 border-slate-200',
  item_resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const PRIORITY_LABELS = { low: 'Bassa', normal: 'Normale', high: 'Alta', urgent: 'Urgente' };
const PRIORITY_TONE = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-slate-100 text-slate-700',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800 font-semibold',
};
const RESOLUTION_LABELS = {
  repaired: 'Riparato',
  replaced: 'Sostituito',
  no_issue: 'Nessun problema rilevato',
  deferred: 'Rimandato',
};

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // attachment_id da mostrare in full
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [resolveItemTarget, setResolveItemTarget] = useState(null); // item_id da risolvere singolarmente
  const [itemResForm, setItemResForm] = useState({ resolution_type: 'repaired', resolution_cost: '', note: '' });
  const [resolvingItem, setResolvingItem] = useState(false);

  const isOps = user?.role === 'operations_manager';
  const isAM = user?.role === 'area_manager';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tk, st, us, atts] = await Promise.all([
        ticketsApi.get(ticketId),
        storesApi.list(),
        (isOps || isAM) ? usersApi.list().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ticketsApi.listAttachments(ticketId).catch(() => ({ data: [] })),
      ]);
      setData(tk.data);
      setStores(Array.isArray(st.data) ? st.data : (st.data?.stores || []));
      setUsers(us.data || []);
      setAttachments(atts.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento ticket');
      navigate('/assistance');
    } finally {
      setLoading(false);
    }
  }, [ticketId, isOps, isAM, navigate]);

  useEffect(() => { load(); }, [load]);

  async function uploadFiles(files) {
    const arr = Array.from(files || []).slice(0, 5);
    if (arr.length === 0) return;
    const remaining = 5 - attachments.length;
    if (remaining <= 0) {
      toast.error('Massimo 5 allegati per ticket');
      return;
    }
    setUploading(true);
    try {
      for (const f of arr.slice(0, remaining)) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name} supera 5MB`);
          continue;
        }
        await ticketsApi.uploadAttachment(ticketId, f);
      }
      toast.success('Allegato caricato');
      const r = await ticketsApi.listAttachments(ticketId);
      setAttachments(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
    }
  }

  async function removeAttachment(attId) {
    if (!window.confirm('Rimuovere questa foto?')) return;
    try {
      await ticketsApi.deleteAttachment(ticketId, attId);
      setAttachments(prev => prev.filter(a => a.attachment_id !== attId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function postComment() {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      await ticketsApi.addComment(ticketId, commentText);
      setCommentText('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setPostingComment(false);
    }
  }

  async function removeComment(cid) {
    if (!window.confirm('Rimuovere questo commento?')) return;
    try {
      await ticketsApi.deleteComment(ticketId, cid);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function changeStatus(newStatus, extra = {}) {
    setChangingStatus(true);
    try {
      await ticketsApi.changeStatus(ticketId, { status: newStatus, ...extra });
      toast.success(`Stato aggiornato: ${STATUS_LABELS[newStatus]}`);
      setShowResolveDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setChangingStatus(false);
    }
  }

  async function updateAssignee(newAssigneeId) {
    try {
      await ticketsApi.update(ticketId, { assignee_id: newAssigneeId || null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function resolveSingleItem() {
    setResolvingItem(true);
    try {
      const payload = { resolution_type: itemResForm.resolution_type };
      if (itemResForm.resolution_cost !== '') payload.resolution_cost = parseFloat(itemResForm.resolution_cost);
      if (itemResForm.note.trim()) payload.note = itemResForm.note.trim();
      const r = await ticketsApi.resolveItem(ticketId, resolveItemTarget, payload);
      if (r.data?.status === 'resolved') {
        toast.success('Ultimo oggetto risolto: ticket chiuso automaticamente');
      } else {
        toast.success('Oggetto risolto');
      }
      setResolveItemTarget(null);
      setItemResForm({ resolution_type: 'repaired', resolution_cost: '', note: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setResolvingItem(false);
    }
  }

  if (loading || !data) return <div className="p-8 text-slate-500">Caricamento…</div>;

  const t = data.ticket;
  const comments = data.comments || [];
  const storeName = stores.find(s => s.store_id === t.store_id)?.display_name
                 || stores.find(s => s.store_id === t.store_id)?.name || t.store_name || '—';
  const assignableUsers = users.filter(u => u.role === 'operations_manager' || u.role === 'area_manager');
  const canEdit = isOps || isAM;
  const canChangeStatus = isOps || isAM;
  const canClose = isOps || (isAM && (t.created_by === user.user_id || t.assignee_id === user.user_id));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ticket-detail-page">
      <PublicHeader copyEnabled={false} />

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-10 space-y-8">
        {/* HEADER PAGINA */}
        <header className="pb-6 border-b border-slate-200">
        <div className="min-w-0 flex-1">
          <Link to="/assistance"
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-3"
                data-testid="back-to-assistance">
            <ArrowLeft className="w-4 h-4" />Tutti i ticket
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            {(t.priority === 'urgent' || t.priority === 'high') && (
              <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
            )}
            <h2 className="font-heading text-3xl font-bold tracking-tight">{t.title}</h2>
            <span className={`text-xs px-2.5 py-1 rounded ${PRIORITY_TONE[t.priority]}`}>
              {PRIORITY_LABELS[t.priority]}
            </span>
            <span className={`text-xs px-3 py-1 rounded-full border ${STATUS_TONE[t.status]}`}
                  data-testid="ticket-status-pill">
              {STATUS_LABELS[t.status]}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Ticket #{t.ticket_id.slice(-8)} · aperto da {t.created_by_email} il {new Date(t.created_at).toLocaleString('it-IT')}
          </p>
        </div>
      </header>

      {/* AZIONI WORKFLOW */}
      {canChangeStatus && t.status !== 'closed' && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="workflow-actions">
          {t.status === 'open' && (
            <Button onClick={() => changeStatus('in_progress')} disabled={changingStatus}
                    className="rounded-md" data-testid="btn-start">
              Inizia lavorazione
            </Button>
          )}
          {(t.status === 'open' || t.status === 'in_progress') && (
            <Button variant="outline" onClick={() => changeStatus('waiting_parts')} disabled={changingStatus}
                    className="rounded-md" data-testid="btn-waiting">
              In attesa ricambi
            </Button>
          )}
          {t.status === 'waiting_parts' && (
            <Button onClick={() => changeStatus('in_progress')} disabled={changingStatus}
                    className="rounded-md" data-testid="btn-resume">
              Riprendi lavorazione
            </Button>
          )}
          {(t.status === 'in_progress' || t.status === 'waiting_parts') && canClose && (
            <Button onClick={() => setShowResolveDialog(true)} disabled={changingStatus}
                    className="rounded-md bg-emerald-700 text-white hover:bg-emerald-800" data-testid="btn-resolve">
              Segna risolto
            </Button>
          )}
          {t.status === 'resolved' && canClose && (
            <Button onClick={() => changeStatus('closed')} disabled={changingStatus}
                    variant="outline" className="rounded-md" data-testid="btn-close">
              Chiudi ticket
            </Button>
          )}
        </div>
      )}

      {/* GRID 2 COLONNE */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* DESCRIZIONE */}
          <DetailCard title="Descrizione" testId="card-description">
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{t.description}</p>
          </DetailCard>

          {/* FOTO ALLEGATE (Fase 2 — 2026-05-19) */}
          <AttachmentsCard
            ticketId={ticketId}
            attachments={attachments}
            uploading={uploading}
            onUpload={uploadFiles}
            onRemove={removeAttachment}
            onOpen={setLightbox}
            currentUserId={user.user_id}
            isOps={isOps}
          />

          {/* COMMENTI */}
          <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="comments-section">
            <div className="flex items-center justify-between">
              <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5" />
                Conversazione
              </h4>
              <span className="text-xs text-slate-500">{comments.length} commenti</span>
            </div>

            {comments.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nessun commento ancora.</p>
            ) : (
              <ul className="space-y-3">
                {comments.map(c => (
                  <li key={c.comment_id} className="flex items-start gap-3 border-l-2 border-slate-300 pl-3"
                      data-testid={`comment-${c.comment_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-500">
                        <span className="font-semibold">{c.created_by_email}</span>
                        {' · '}{new Date(c.created_at).toLocaleString('it-IT')}
                      </p>
                      <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{c.text}</p>
                    </div>
                    {(isOps || c.created_by === user.user_id) && (
                      <button onClick={() => removeComment(c.comment_id)}
                              className="text-slate-400 hover:text-red-600 transition"
                              data-testid={`remove-comment-${c.comment_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Composer */}
            <div className="flex items-start gap-2 pt-2" data-testid="comment-composer">
              <Textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Scrivi un commento, un aggiornamento o un dettaglio tecnico..."
                rows={2}
                className="flex-1"
                data-testid="comment-input"
              />
              <Button onClick={postComment} disabled={postingComment || !commentText.trim()}
                      className="self-stretch px-4" data-testid="comment-submit">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </section>

          {/* STORICO STATI */}
          {t.status_history && t.status_history.length > 1 && (
            <DetailCard title="Storico stati" testId="card-history">
              <ul className="space-y-2">
                {t.status_history.slice().reverse().map((h, idx) => (
                  <li key={`${h.status}-${h.at}-${idx}`} className="flex items-start gap-2 text-xs">
                    <History className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium border ${STATUS_TONE[h.status]}`}>
                        {STATUS_LABELS[h.status]}
                      </span>
                      <span className="ml-2 text-slate-500">
                        {new Date(h.at).toLocaleString('it-IT')}
                      </span>
                      {h.resolution_type && (
                        <span className="ml-2 text-emerald-700">
                          → {RESOLUTION_LABELS[h.resolution_type]}
                          {h.resolution_cost != null && ` · €${Number(h.resolution_cost).toFixed(2)}`}
                        </span>
                      )}
                      {h.note && <p className="text-slate-700 mt-0.5">{h.note}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </DetailCard>
          )}
        </div>

        {/* SIDEBAR */}
        <div className="space-y-5">
          <DetailCard title="Dettagli" testId="card-details">
            <SidebarRow icon={StoreIcon} label="Negozio"
                        value={<Link to={`/stores/${t.store_id}`} className="text-slate-900 hover:underline font-medium" data-testid="link-store">{storeName}</Link>} />
            {(t.item_ids?.length > 0) && (
              <SidebarRow icon={Package}
                          label={t.item_ids.length > 1
                            ? `Oggetti Magazzino (${Object.keys(t.item_resolutions || {}).length}/${t.item_ids.length} risolti)`
                            : 'Oggetto Magazzino'}
                          value={
                            <span className="flex flex-col gap-1">
                              {t.item_ids.map((iid, idx) => {
                                const res = t.item_resolutions?.[iid];
                                const canResolveThis = canClose && !['resolved', 'closed'].includes(t.status) && !res;
                                return (
                                  <span key={iid} className="flex items-center gap-2 flex-wrap">
                                    <Link to={`/inventory/${iid}`} className="text-slate-900 hover:underline font-mono text-sm"
                                          data-testid={idx === 0 ? 'link-item' : `link-item-${idx}`}>
                                      {t.item_codes?.[idx] || iid}
                                    </Link>
                                    {res && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded"
                                            title={res.note || ''} data-testid={`item-resolved-badge-${iid}`}>
                                        ✓ {RESOLUTION_LABELS[res.resolution_type] || res.resolution_type}
                                        {res.resolution_cost != null ? ` · €${res.resolution_cost}` : ''}
                                      </span>
                                    )}
                                    {canResolveThis && (
                                      <button type="button"
                                              className="text-[11px] text-blue-700 hover:underline"
                                              onClick={() => setResolveItemTarget(iid)}
                                              data-testid={`resolve-item-btn-${iid}`}>
                                        Risolvi
                                      </button>
                                    )}
                                  </span>
                                );
                              })}
                            </span>
                          } />
            )}
            {t.category && <SidebarRow icon={Sparkles} label="Categoria" value={t.category} />}
            {t.due_date && (
              <SidebarRow icon={Calendar} label="Scadenza"
                          value={new Date(t.due_date).toLocaleDateString('it-IT')} />
            )}
            <SidebarRow icon={User2} label="Aperto da" value={t.created_by_email} />
            <SidebarRow icon={Clock} label="Ultimo aggiornamento"
                        value={new Date(t.updated_at).toLocaleString('it-IT')} />
          </DetailCard>

          {/* Assegnatario */}
          <DetailCard title="Assegnatario" testId="card-assignee">
            {canEdit ? (
              <Select value={t.assignee_id || 'none'}
                      onValueChange={(v) => updateAssignee(v === 'none' ? null : v)}>
                <SelectTrigger data-testid="assignee-select"><SelectValue placeholder="— nessuno —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— nessuno —</SelectItem>
                  {assignableUsers.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.email} ({u.role === 'operations_manager' ? 'OPS' : 'AM'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm">{t.assignee_email || <span className="italic text-slate-400">Non assegnato</span>}</p>
            )}
          </DetailCard>
        </div>
      </div>

      {showResolveDialog && (
        <ResolveDialog
          ticket={t}
          onClose={() => setShowResolveDialog(false)}
          onConfirm={(extra) => changeStatus('resolved', extra)}
          loading={changingStatus}
        />
      )}
      {resolveItemTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setResolveItemTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="resolve-item-dialog">
            <DialogHeader>
              <DialogTitle>Risolvi oggetto</DialogTitle>
              <DialogDescription>
                <code className="font-mono">{t.item_codes?.[t.item_ids?.indexOf(resolveItemTarget)] || resolveItemTarget}</code> — gli altri oggetti del ticket restano aperti. Quando tutti sono risolti, il ticket si chiude da solo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Esito</Label>
                <Select value={itemResForm.resolution_type}
                        onValueChange={(v) => setItemResForm(f => ({ ...f, resolution_type: v }))}>
                  <SelectTrigger data-testid="resolve-item-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="repaired">Riparato</SelectItem>
                    <SelectItem value="replaced">Sostituito</SelectItem>
                    <SelectItem value="no_issue">Nessun problema rilevato</SelectItem>
                    <SelectItem value="deferred">Rimandato</SelectItem>
                  </SelectContent>
                </Select>
                {(itemResForm.resolution_type === 'repaired' || itemResForm.resolution_type === 'replaced') && (
                  <p className="text-[11px] text-emerald-700 mt-1.5">
                    Verrà aggiunto un evento di {itemResForm.resolution_type === 'repaired' ? 'manutenzione' : 'ricambio'} alla scheda Magazzino di questo oggetto.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Costo intervento (€)</Label>
                <Input type="number" step="0.01" value={itemResForm.resolution_cost}
                       onChange={e => setItemResForm(f => ({ ...f, resolution_cost: e.target.value }))}
                       placeholder="0.00" data-testid="resolve-item-cost" />
              </div>
              <div>
                <Label className="text-xs">Nota (opzionale)</Label>
                <Textarea rows={2} value={itemResForm.note}
                          onChange={e => setItemResForm(f => ({ ...f, note: e.target.value }))}
                          placeholder="Dettagli sull'intervento..." data-testid="resolve-item-note" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResolveItemTarget(null)}>Annulla</Button>
              <Button onClick={resolveSingleItem} disabled={resolvingItem}
                      className="bg-emerald-700 text-white hover:bg-emerald-800"
                      data-testid="resolve-item-confirm">
                {resolvingItem ? 'Salvataggio…' : 'Risolvi oggetto'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {lightbox && (
        <Lightbox
          ticketId={ticketId}
          attachment={attachments.find(a => a.attachment_id === lightbox)}
          onClose={() => setLightbox(null)}
        />
      )}
      </div>
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================
function DetailCard({ title, children, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testId}>
      <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-4">{title}</h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SidebarRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</p>
        <div className="text-sm text-slate-800">{value}</div>
      </div>
    </div>
  );
}

function ResolveDialog({ ticket, onClose, onConfirm, loading }) {
  const [resolutionType, setResolutionType] = useState('repaired');
  const [resolutionCost, setResolutionCost] = useState('');
  const [note, setNote] = useState('');

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Segna come risolto</DialogTitle>
          <DialogDescription>
            Scegli il tipo di intervento e indica il costo. Se il ticket è collegato a un oggetto Magazzino, l'evento di manutenzione o ricambio viene registrato automaticamente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Tipo di risoluzione *</Label>
            <Select value={resolutionType} onValueChange={setResolutionType}>
              <SelectTrigger data-testid="resolve-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="repaired">Riparato</SelectItem>
                <SelectItem value="replaced">Sostituito</SelectItem>
                <SelectItem value="no_issue">Nessun problema rilevato</SelectItem>
                <SelectItem value="deferred">Rimandato a data da definire</SelectItem>
              </SelectContent>
            </Select>
            {(ticket.item_ids?.length > 0 || ticket.item_id) && (resolutionType === 'repaired' || resolutionType === 'replaced') && (
              <p className="text-[11px] text-emerald-700 mt-1.5">
                Verrà aggiunto automaticamente un evento di {resolutionType === 'repaired' ? 'manutenzione' : 'ricambio'} alla scheda Magazzino di {ticket.item_ids?.length > 1 ? `tutti i ${ticket.item_ids.length} oggetti collegati` : "dell'oggetto collegato"}.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Costo dell'intervento (€)</Label>
            <Input type="number" step="0.01" value={resolutionCost}
                   onChange={e => setResolutionCost(e.target.value)}
                   placeholder="0.00"
                   data-testid="resolve-cost" />
          </div>
          <div>
            <Label className="text-xs">Nota di chiusura (opzionale)</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Dettagli sull'intervento..."
                      data-testid="resolve-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onConfirm({
                    resolution_type: resolutionType,
                    resolution_cost: resolutionCost ? Number(resolutionCost) : null,
                    note: note || null,
                  })}
                  disabled={loading}
                  className="bg-emerald-700 text-white hover:bg-emerald-800"
                  data-testid="resolve-confirm">
            {loading ? 'Salvataggio…' : 'Conferma risoluzione'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// Attachments card + Lightbox (idea Fase 2 Allegati Foto)
// ============================================================
function AttachmentsCard({ ticketId, attachments, uploading, onUpload, onRemove, onOpen, currentUserId, isOps }) {
  const fileInputRef = React.useRef(null);
  const remaining = Math.max(0, 5 - attachments.length);

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="attachments-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" />
          Foto allegate
          <span className="text-slate-400 normal-case tracking-normal font-normal">
            ({attachments.length}/5)
          </span>
        </h4>
        {remaining > 0 && (
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="sm"
            variant="outline"
            className="rounded-md"
            data-testid="upload-attachment-btn"
          >
            <Upload className={`w-3.5 h-3.5 mr-1.5 ${uploading ? 'animate-pulse' : ''}`} />
            {uploading ? 'Caricamento…' : 'Aggiungi foto'}
          </Button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
        multiple
        className="hidden"
        onChange={(e) => { onUpload(e.target.files); e.target.value = ''; }}
        data-testid="attachment-file-input"
      />

      {attachments.length === 0 ? (
        <p className="text-sm text-slate-500 italic">
          Nessuna foto. Aggiungi un'immagine del guasto per dare contesto a chi interverrà.
          <span className="block text-[11px] mt-1 text-slate-400">JPG, PNG, HEIC o WEBP — max 5MB ciascuna, fino a 5 foto.</span>
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="attachments-grid">
          {attachments.map(att => {
            const url = ticketsApi.attachmentUrl(ticketId, att.attachment_id);
            const canDelete = isOps || att.uploaded_by === currentUserId;
            return (
              <div key={att.attachment_id} className="relative group border border-slate-200 bg-slate-50 aspect-square overflow-hidden"
                   data-testid={`attachment-${att.attachment_id}`}>
                <button
                  type="button"
                  onClick={() => onOpen(att.attachment_id)}
                  className="w-full h-full block"
                  aria-label={`Apri ${att.filename}`}
                >
                  <img
                    src={url}
                    alt={att.filename}
                    loading="lazy"
                    className="w-full h-full object-cover hover:opacity-90 transition"
                  />
                </button>
                {canDelete && (
                  <button
                    onClick={() => onRemove(att.attachment_id)}
                    className="absolute top-1.5 right-1.5 bg-white/90 border border-slate-200 p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-600 hover:text-red-600 transition"
                    aria-label="Rimuovi foto"
                    data-testid={`attachment-remove-${att.attachment_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Lightbox({ ticketId, attachment, onClose }) {
  React.useEffect(() => {
    if (!attachment) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachment, onClose]);
  if (!attachment) return null;
  const url = ticketsApi.attachmentUrl(ticketId, attachment.attachment_id);
  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="attachment-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="Chiudi"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={url}
        alt={attachment.filename}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-[90vh] object-contain shadow-2xl"
      />
    </div>
  );
}
